// src/store/slices/employeeContactSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

// ---------------- ASYNC THUNKS ---------------- //

// Fetch all contacts for an employee
export const fetchContactsByEmployeeId = createAsyncThunk(
  'employeeContact/fetchByEmployeeId',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/employee/contact/${employeeId}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Create a new contact
export const createEmployeeContact = createAsyncThunk(
  'employeeContact/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/employee/contact`, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update an existing contact
export const updateEmployeeContact = createAsyncThunk(
  'employeeContact/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/employee/contact/${id}`, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Delete a contact
export const deleteEmployeeContact = createAsyncThunk(
  'employeeContact/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/employee/contact/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// ---------------- SLICE ---------------- //
const employeeContactSlice = createSlice({
  name: 'employeeContact',
  initialState: {
    contacts: [],
    status: 'idle',
    error: null,
  },
  reducers: {
    clearContacts: (state) => {
      state.contacts = [];
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchContactsByEmployeeId.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchContactsByEmployeeId.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contacts = action.payload;
      })
      .addCase(fetchContactsByEmployeeId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // Create
      .addCase(createEmployeeContact.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createEmployeeContact.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contacts.push(action.payload);
      })
      .addCase(createEmployeeContact.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // Update
      .addCase(updateEmployeeContact.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateEmployeeContact.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contacts = state.contacts.map((c) =>
          c.id === action.payload.id ? action.payload : c
        );
      })
      .addCase(updateEmployeeContact.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // Delete
      .addCase(deleteEmployeeContact.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteEmployeeContact.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.contacts = state.contacts.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteEmployeeContact.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      });
  },
});

export const { clearContacts } = employeeContactSlice.actions;
export default employeeContactSlice.reducer;
